import { pdf } from "@react-pdf/renderer";
import { getISOWeek } from "date-fns";
import {
  getHistoryActivity,
  getStatisticsCollections,
  getTemporalData,
  getWasteStatistic,
} from "../../../services/container";
import { getCorporationData } from "../../../services/corporation";
import { azureSASTokenToUrl } from "../../../components/formatted/FormattedAzureSasToken";
import { formattedDateDocument } from "../../../components/formatted/FormattedDateAndTime";
import { downloadFile, reduceImageQuality } from "../../../shared/utils/utils";
import { mergeFluxes } from "../utils/statisticsUtils";
import {
  buildWeeklyReportModel,
  filterCollectionsByPeriod,
  getSixMonthBaselineRange,
  resolveWeeklyPeriod,
  selectPhotosByFlux,
} from "./weeklyReportModel";
import WeeklyReportPdfDocument from "./WeeklyReportPdfDocument";

const PHOTO_CONCURRENCY = 3;

const getMaterialsMergeConfig = () => {
  try {
    const materials =
      JSON.parse(localStorage.getItem("materialsDescription")) || [];
    return materials.map((material) => ({
      referenceName: material.referenceName,
      possibleNames: material.possibleNames,
    }));
  } catch (error) {
    return [];
  }
};

const slugify = (value) =>
  String(value || "site")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .toLowerCase() || "site";

const keywordValue = (keywordFilter) =>
  keywordFilter && typeof keywordFilter === "object" && "value" in keywordFilter
    ? keywordFilter.value
    : keywordFilter || [];

const readJsonResponse = (response, fallback) => {
  if (response?.status !== 200) return fallback;
  return response.data ?? fallback;
};

const mapWithConcurrency = async (items, limit, mapper) => {
  const results = new Array(items.length);
  let cursor = 0;
  const workers = Array.from(
    { length: Math.min(limit, items.length) },
    async () => {
      while (cursor < items.length) {
        const index = cursor;
        cursor += 1;
        results[index] = await mapper(items[index], index);
      }
    },
  );
  await Promise.all(workers);
  return results;
};

const getImageDimensions = async (src) => {
  try {
    const { default: reactImageSize } = await import("react-image-size");
    if (!src) return { width: 0, height: 0 };
    return await reactImageSize(src);
  } catch (error) {
    console.error("Weekly report image size failed:", error);
    return { width: 0, height: 0 };
  }
};

const enrichPhoto = async (row) => {
  const containerId = row._id || row._measurement;
  let wasteObjectsList = [];
  try {
    const response = await getTemporalData({ time: row._time }, containerId);
    if (response.status === 200) {
      wasteObjectsList = (
        response.data?.temporalData?.[0]?.anomaliesObjects || []
      ).filter(
        (object) =>
          Boolean(object?.cls) &&
          Array.isArray(object?.points_konva) &&
          object.points_konva.length > 0,
      );
    }
  } catch (error) {
    console.error("Weekly report temporal data failed:", error);
  }

  const imageUrl = azureSASTokenToUrl(row.image_url);
  const imageDimensions = await getImageDimensions(imageUrl);
  let compressedImage = null;
  try {
    compressedImage = await reduceImageQuality(imageUrl, 0.4);
  } catch (error) {
    console.error("Weekly report image compress failed:", error);
  }

  const hasCrop = Number(row.w_box) > 0 && Number(row.h_box) > 0;
  return {
    ...row,
    wasteObjectsList,
    imageDimensions,
    compressedImage,
    x_box: hasCrop ? Number(row.x_box) : 0,
    y_box: hasCrop ? Number(row.y_box) : 0,
    w_box: hasCrop ? Number(row.w_box) : imageDimensions.width,
    h_box: hasCrop ? Number(row.h_box) : imageDimensions.height,
  };
};

export const exportWeeklyReportPdf = async ({
  periodStart,
  periodStop,
  keywordFilter,
  siteName,
  t,
  onProgress,
}) => {
  const reportProgress = (percent, labelKey) => {
    if (typeof onProgress === "function") {
      onProgress({
        percent,
        label: t(labelKey),
      });
    }
  };

  reportProgress(8, "weeklyReport.progress.stats");
  const weeklyPeriod = resolveWeeklyPeriod(periodStart, periodStop);
  const baseline = getSixMonthBaselineRange(weeklyPeriod.stop);
  const filterPayload = JSON.stringify(keywordValue(keywordFilter));

  const [
    baselineCollectionsResponse,
    qualityResponse,
    baselineQualityResponse,
    corporationResponse,
  ] = await Promise.all([
    getStatisticsCollections({
      periodStart: baseline.start,
      periodStop: baseline.stop,
      keywordFilter: filterPayload,
    }),
    getWasteStatistic({
      periodStart: weeklyPeriod.start,
      periodStop: weeklyPeriod.stop,
      keywordFilter: filterPayload,
    }),
    getWasteStatistic({
      periodStart: baseline.start,
      periodStop: baseline.stop,
      keywordFilter: filterPayload,
    }),
    getCorporationData(),
  ]);

  const baselineCollections = readJsonResponse(baselineCollectionsResponse, {});
  const quality = readJsonResponse(qualityResponse, {}).statisticsData || {};
  const baselineQuality =
    readJsonResponse(baselineQualityResponse, {}).statisticsData || {};
  const corporation = corporationResponse?.data?.corporation || {};
  const mergeConfig = getMaterialsMergeConfig();
  const allCollections = Array.isArray(baselineCollections.collectionsData)
    ? baselineCollections.collectionsData
    : [];
  const mergedSortingErrors = mergeFluxes(
    quality.dataSortingError,
    mergeConfig,
  );

  const model = buildWeeklyReportModel({
    collectionsData: filterCollectionsByPeriod(
      allCollections,
      weeklyPeriod.start,
      weeklyPeriod.stop,
    ),
    dataResult: {},
    baselineDataResult: baselineCollections.dataResult || {},
    monthlyCaracterisation: quality.monthlyCaracterisation || [],
    baselineMonthlyCaracterisation:
      baselineQuality.monthlyCaracterisation || [],
    dataSortingError: mergedSortingErrors,
    baselineDataSortingError: mergeFluxes(
      baselineQuality.dataSortingError,
      mergeConfig,
    ),
  });

  reportProgress(30, "weeklyReport.progress.photos");
  let historyList = [];
  try {
    const historyResponse = await getHistoryActivity({
      periodStart: weeklyPeriod.start,
      periodStop: weeklyPeriod.stop,
      displayAnomalie: true,
      keywordFilter: filterPayload,
      toDownload: true,
    });
    historyList = historyResponse?.data?.historyList || [];
  } catch (error) {
    console.error("Weekly report history fetch failed:", error);
  }

  const selectedPhotos = selectPhotosByFlux(historyList);
  const flatPhotos = Object.values(selectedPhotos).flat();
  const enrichedPhotos = await mapWithConcurrency(
    flatPhotos,
    PHOTO_CONCURRENCY,
    enrichPhoto,
  );
  const photosByFlux = {};
  Object.entries(selectedPhotos).forEach(([flux, rows]) => {
    const enriched = rows
      .map((row) => {
        const index = flatPhotos.indexOf(row);
        return enrichedPhotos[index];
      })
      .filter((photo) => Number(photo?.imageDimensions?.width) > 0);
    if (enriched.length > 0) {
      photosByFlux[flux] = enriched;
    }
  });

  reportProgress(80, "weeklyReport.progress.pdf");
  const stopDate = new Date(weeklyPeriod.stop);
  const blob = await pdf(
    <WeeklyReportPdfDocument
      corporationName={corporation.corporationName}
      corporationLogoUrl={corporation.logoUrl}
      siteName={siteName}
      weekNumber={getISOWeek(stopDate)}
      year={stopDate.getFullYear()}
      periodStart={weeklyPeriod.start}
      periodStop={weeklyPeriod.stop}
      model={model}
      photosByFlux={photosByFlux}
      dataVolume={quality.dataVolume || {}}
    />,
  ).toBlob();

  const fileName = `AK_Bilan_${slugify(siteName)}_${formattedDateDocument(weeklyPeriod.start).replace(/\//g, "-")}_${formattedDateDocument(weeklyPeriod.stop).replace(/\//g, "-")}.pdf`;
  reportProgress(96, "weeklyReport.progress.pdf");
  await downloadFile(blob, fileName);
  reportProgress(100, "weeklyReport.progress.pdf");
};
